import {
    createContext,
    useContext,
    useEffect,
    useId,
    useRef,
    type ComponentPropsWithRef,
    type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'
import { ScreenOverlay } from '@/React/Shared/Component/ScreenOverlay'
import { Icon, IconNameEnum } from '@/React/Shared/Component/Icon'
import { ButtonSquareIcon } from '@/React/Shared/Component/ButtonSquareIcon'
import { useFocusTrap } from '@/React/Shared/Hook/useFocusTrap'

interface DrawerContextValue {
    onClose?: () => void
    titleId?: string
}

const DrawerContext = createContext<DrawerContextValue>({})

export interface DrawerContainerProps extends Omit<
    ComponentPropsWithRef<'div'>,
    'title' | 'content'
> {
    show?: boolean
    loading?: boolean
    width?: 'standard' | 'wide'
    side?: 'left' | 'right'
    onClose?: () => void
    onOpen?: () => void
    children?: ReactNode
}

export function DrawerContainer({
    show = false,
    loading = false,
    width = 'standard',
    side = 'right',
    onClose,
    onOpen,
    children,
    className = '',
    ...restProps
}: DrawerContainerProps) {
    const isFirstRender = useRef(true)

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false

            if (show) {
                onOpen?.()
            }

            return
        }

        if (show) {
            onOpen?.()
        } else {
            onClose?.()
        }
    }, [show, onOpen, onClose])

    const generatedTitleId = useId()
    const ariaLabel = restProps['aria-label']
    const titleId =
        (restProps['aria-labelledby'] as string | undefined) ??
        `drawer-title-${generatedTitleId}`
    const ariaLabelledby =
        restProps['aria-labelledby'] ?? (ariaLabel ? undefined : titleId)
    const trapRef = useFocusTrap<HTMLDivElement>(show, onClose)

    if (!show) {
        return null
    }

    const drawerWidthClass = width === 'wide' ? 'md:w-180' : 'md:w-md'
    const drawerSideClass = side === 'left' ? 'left-0' : 'right-0'
    const drawerContent = (
        <DrawerContext.Provider value={{ onClose, titleId }}>
            <ScreenOverlay onClick={onClose} />

            <div
                ref={trapRef}
                tabIndex={-1}
                role="dialog"
                aria-modal="true"
                aria-labelledby={ariaLabelledby}
                {...restProps}
                className={`z-50 h-full fixed top-0 bg-white w-11/12 outline-none ${drawerWidthClass} ${drawerSideClass} ${className}`.trim()}
            >
                <div className="h-full w-full relative">
                    {loading ? <DrawerLoading /> : null}

                    <div className="h-full flex flex-col justify-between">
                        {children}
                    </div>
                </div>
            </div>
        </DrawerContext.Provider>
    )

    if (typeof document === 'undefined') {
        return drawerContent
    }

    const target =
        document.getElementById('vue-teleport-container') || document.body
    return createPortal(drawerContent, target)
}

export interface DrawerHeaderProps extends ComponentPropsWithRef<'div'> {
    showCloseButton?: boolean
    children?: ReactNode
}

export function DrawerHeader({
    showCloseButton = true,
    children,
    className = '',
    ...restProps
}: DrawerHeaderProps) {
    const { onClose } = useContext(DrawerContext)

    return (
        <>
            <div
                {...restProps}
                className={`p-6 text-lg font-semibold text-gray-800 flex justify-between items-center ${className}`.trim()}
            >
                <div>{children ?? <div />}</div>
                {showCloseButton ? (
                    <ButtonSquareIcon
                        onClick={onClose}
                        aria-label="Close"
                    >
                        <Icon
                            name={IconNameEnum.Close}
                            className="w-5 h-5"
                            aria-hidden="true"
                        />
                    </ButtonSquareIcon>
                ) : null}
            </div>
            <div className="px-6 pb-6">
                <div className="border-b border-b-gray-200"></div>
            </div>
        </>
    )
}

export function DrawerTitle({
    children,
    className = '',
    ...restProps
}: ComponentPropsWithRef<'div'>) {
    const { titleId } = useContext(DrawerContext)

    return (
        <div
            id={restProps.id ?? titleId}
            {...restProps}
            className={`text-lg font-semibold text-gray-800 ${className}`.trim()}
        >
            {children}
        </div>
    )
}

export interface DrawerContentProps extends ComponentPropsWithRef<'div'> {
    children?: ReactNode
}

export function DrawerContent({
    children,
    className = '',
    ...restProps
}: DrawerContentProps) {
    return (
        <div
            {...restProps}
            className={`text-gray-700 h-full overflow-y-auto px-6 ${className}`.trim()}
        >
            <div className="h-full">{children}</div>
        </div>
    )
}

export function DrawerActions({
    children,
    className = '',
    ...restProps
}: ComponentPropsWithRef<'div'>) {
    return (
        <div
            {...restProps}
            className={`p-6 ${className}`.trim()}
        >
            <div className="pt-6 border-t border-t-gray-200 flex justify-end gap-2">
                {children}
            </div>
        </div>
    )
}

export function DrawerLoading() {
    return (
        <>
            <div className="absolute top-0 left-0 w-full h-full bg-gray-900 opacity-30 z-10" />
            <div className="absolute top-0 left-0 w-full h-full z-20 flex items-center justify-center">
                <div className="flex flex-col gap-4 justify-center items-center">
                    <Icon
                        name={IconNameEnum.LoadingSpinner}
                        className="w-8 h-8 animate-spin text-gray-900"
                    />
                    <div className="text-lg font-bold text-gray-900">
                        Loading...
                    </div>
                </div>
            </div>
        </>
    )
}

export const Drawer = Object.assign(DrawerContainer, {
    Container: DrawerContainer,
    Header: DrawerHeader,
    Title: DrawerTitle,
    Content: DrawerContent,
    Actions: DrawerActions,
    Loading: DrawerLoading,
})
